<template>
	<div style="height:100%" class="flex flex_between flex_column">
		<h4 class="font_lg font_bold margin_top">{{ $t("common.invoiceDetails") }}</h4>
		<div v-if="form.rejectionReason" class="error_hint_box w_100 base_margin">
			<span class="font_bold">{{ $t("common.hint") }}:&nbsp;</span>
			<span v-html="$t('seller.upload.rejectionReason.note')"></span>
			<br>
			<br>
			<span class="font_bold">{{ $t("common.rejectionReason") }}:</span>
			<br>
			<span class="font_italic" v-html="formatAsHtml(form.rejectionReason)"></span>
		</div>
		<div v-if="invoiceNumberCheckError" class="error_hint_box w_100 base_margin" ref="invoiceNumberCheckErrorBox">
			<span class="font_bold">{{ $t("common.hint") }}:&nbsp;</span>
			<span v-html="invoiceNumberCheckError"></span>
		</div>
		<div v-else-if="this.wasProspective" class="text_left light_bg_color base_margin">
			<span class="black_color" v-html="$t('seller.upload.prospectiveReceivableConverted.note')"></span>
		</div>
		<form class="flex_1" style="width:100%" action>
			<div class="md-layout">
				<div class="md-layout-item flex flex_column flex_between">
					<div style="width:100%">
						<AvailableDebtorSelect
							:value="debtorId"
							:api="loadAvailableDebtors"
							v-on:input="changeDebtor"
							v-on:redirect="beforeRedirect"
							:required="true"
							data-vv-name="debtorData"
							v-validate="modelValidations.debtorData"
							:error="errors.first('debtorData')"
							:disabled="wasProspective"
						></AvailableDebtorSelect>
						<DebtorContactSelect
							:value="contactData"
							v-on:input="changeContact"
							:required="true"
							data-vv-name="contactData"
							v-validate="modelValidations.contactData"
							:error="errors.first('contactData')"
						></DebtorContactSelect>
					</div>
					<div v-if="isIpuAllowed" class="flex flex_column" style="width:100%;">
						<md-checkbox v-model="form.requestObligorConfirmation">
							{{ $t("seller.upload.Request") }}
							<el-tooltip :content="$t('seller.upload.ipuRequest.tooltip')" placement="top">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-checkbox>
					</div>
					<div style="width:100%; margin-top: 16px;">
						<TypeSelect :typeId="form.type" v-on:onSelect="selectType" :exclude="[enumDef.receivableType.InstallmentContractRate]"></TypeSelect>
						<md-field :class="[{ 'md-error': errors.has('invoiceNumber') }]">
							<label v-if="!prospective">{{ $t("seller.upload.InvoiceNumber") }}</label>
							<label v-else>{{ $t("seller.upload.prospective.invoiceNumber") }}</label>
							<md-input v-model="form.invoiceNumber"
									  required
									  data-vv-name="invoiceNumber"
									  v-validate="modelValidations.invoiceNumber"
							></md-input>
							<span v-if="errors.has('invoiceNumber')"
								  class="validation-error">{{ errors.first("invoiceNumber") }}</span>
						</md-field>
						<div class="flex">
							<md-field :class="[{ 'md-error': errors.has('amount') }]">
								<label v-if="!prospective">{{ $t("seller.upload.Amount") }}</label>
								<label v-else>{{ $t("seller.upload.prospective.amount") }}</label>
								<MoneyAmountInput v-model="form.amount"
												  @input="onAmountChanged"
												  :required="true"
												  data-vv-name="amount"
												  v-validate="modelValidations.amount"
								></MoneyAmountInput>
								<span v-if="errors.has('amount')" class="validation-error">{{ errors.first("amount") }}</span>
							</md-field>
							<div class="margin_r"></div>
							<currency-select class="w_33"
								:value="form.currency"
								:isError="!form.currency&&touched"
								v-on:input="selectCurrency"
								:required="true"
								data-vv-name="currency"
								v-validate="modelValidations.currency"
								:error="errors.first('currency')"
								:is-for-prospective-upload="prospective"
							></currency-select>
						</div>
						<div class="flex">
							<DatePicker class="flex_1"
								v-on:input="changeInvoiceDate"
								:value="form.invoiceDate"
								valueFormat="dd/MM/yyyy"
								:placeholder="!this.prospective ? this.$t('seller.upload.InvoiceDate') : this.$t('seller.upload.prospective.invoiceDate')"
								required
								data-vv-name="invoiceDate"
								v-validate="modelValidations.invoiceDate"
								:error="errors.first('invoiceDate')"
							></DatePicker>
							<div class="margin_r"></div>
							<DatePicker class="flex_1"
								v-on:input="changeDueDate"
								:value="form.dueDate"
								valueFormat="dd/MM/yyyy"
								:placeholder="!prospective ? $t('seller.upload.DueDate') : $t('seller.upload.prospective.dueDate')"
								required
								data-vv-name="dueDate"
								v-validate="modelValidations.dueDate"
								:error="errors.first('dueDate')"
							></DatePicker>
						</div>
						<CountrySelect
							:labelText="$t('seller.upload.ApplicableLaw')"
							v-model="form.applicableLaw"
							:companyType=enumDef.companyType.DebtorCompany
							:required="true"
							data-vv-name="applicableLaw"
							v-validate="modelValidations.applicableLaw"
							:error="errors.first('applicableLaw')"
							:tooltip-text="$t('seller.upload.applicableLaw.tooltip')"
						></CountrySelect>
						<PdfFormDocuments
							class="margin_top"
							ref="documents"
							:validator="$validator"
						/>
						<h4 class="font_lg font_bold margin_top">{{ $t("seller.upload.salesParameters") }}</h4>
						<AssignmentTypeSelect
							v-model="form.disclosed"
							:assignment-type-from-dsr="assignmentTypeFromDsr"
							:receivable-type="form.type"
							:required="true"
							data-vv-name="disclosed"
							v-validate="modelValidations.disclosed"
							:error="errors.first('disclosed')"
						></AssignmentTypeSelect>

						<!-- Predefined pricing -->
						<div v-if="hasPredefinedSaleParameters" class="flex padding_bottom_l">
							<md-button v-if="!sellPriceSuggestion.serviceFeeRate" class="md-primary" @click="suggestPrice">
								{{ $t("seller.upload.salesParam.button.previewPrice") }}
							</md-button>
							<div v-else class="flex_1 gray_very_light_background_color padding_horizontal padding_vertical">
								<table>
									<tr>
										<td class="font_bold text_left">{{ $t("seller.upload.price.interest.label") }}:</td>
										<td class="text_left">
											{{ $t("seller.upload.price.interest.value", {
													interestInPercent: sellPriceSuggestion.formatted.interestBaseRatePerAnnum,
													referenceInterestName: $t("common.referenceInterestRate." + form.currency)
												})
											}}
											<el-tooltip :open-delay="300" placement="top"
														:content="$t('common.referenceInterestRate.' + form.currency + '.tooltip')">
												<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
											</el-tooltip>
										</td>
									</tr>
									<tr>
										<td class="font_bold text_left">{{ $t("seller.upload.price.serviceFee.label") }}:</td>
										<td class="text_left">{{ sellPriceSuggestion.formatted.serviceFeeRate }}</td>
									</tr>
								</table>
								<hr>
								<table class="w_100">
									<template v-if="retentionCheck.sellWithRetention">
										<tr>
											<td class="text_left font_bold">
												{{ $t("seller.upload.price.withRetention.label", { retentionInPercent: retentionCheck.textParams.retentionInPercent }) }}
											</td>
										</tr>
										<tr style="vertical-align: top">
											<td class="font_bold text_left valid_color">
												{{ $t("seller.upload.price.instantPayout") }}:
											</td>
											<td class="text_right">{{ sellPriceSuggestion.formatted.instantPayoutInclRetention }}</td>
										</tr>
										<tr style="vertical-align: top">
											<td class="font_bold text_left valid_color">{{ $t("seller.upload.price.withRetention.retentionPayout") }}:</td>
											<td class="text_right">{{ sellPriceSuggestion.formatted.retentionPayout }}</td>
										</tr>
									</template>
									<template v-else>
										<tr style="vertical-align: top">
											<td class="font_bold text_left valid_color">
												{{ $t("seller.upload.price.instantPayout") }}:
											</td>
											<td class="text_right">{{ sellPriceSuggestion.formatted.instantPayoutInclRetention }}</td>
										</tr>
									</template>
								</table>
							</div>
						</div>
						<!-- User defined pricing -->
						<div v-else>
							<md-radio class="w_100" style="margin-bottom: 0;"
									  v-model="form.sellPriceCalculationType"
									  :value="enumDef.sellPriceCalculationType.None"
							>
								{{ $t("seller.upload.salesParam.none") }}
							</md-radio>
							<UploadSalesParam v-model="form.sellPriceDiscountRate"
											  :selected-type="form.sellPriceCalculationType"
											  :type-value="enumDef.sellPriceCalculationType.Discount"
											  :suggested-price="sellPriceSuggestion.discountRate"
											  :show-unknown-rating-hint="!sellPriceSuggestion.debtorHasKnownRating"
											  @select="selectSellPriceCalculationType"
											  @suggest="suggestPrice"
											  name-translation-key="seller.upload.salesParam.discount"
											  data-vv-name="sellPriceDiscountRate"
											  v-validate="modelValidations.sellPriceDiscountRate"
											  :error="errors.first('sellPriceDiscountRate')"
							/>
							<UploadSalesParam  v-model="form.sellPriceCostOfCapitalRate"
											   :selected-type="form.sellPriceCalculationType"
											   :type-value="enumDef.sellPriceCalculationType.CostOfCapital"
											   :suggested-price="sellPriceSuggestion.costOfCapitalRate"
											   :show-unknown-rating-hint="!sellPriceSuggestion.debtorHasKnownRating"
											   @select="selectSellPriceCalculationType"
											   @suggest="suggestPrice"
											   name-translation-key="seller.upload.salesParam.costOfCapital"
											   :formula-url="costOfCapitalFormulaUrl"
							/>
						</div>
						<RetentionCheck
							v-model="form.sellWithRetention"
							:debtor-id="debtorId"
							:validation-rules="modelValidations.sellWithRetention"
							:validator="$validator"
							@update="updateRetentionCheck"
						/>
						<NoSanctionsCheck
							v-model="form.noSanctionsConfirmation"
							:validation-rules="modelValidations.noSanctionsConfirmation"
							:validator="$validator"
						/>
					</div>
				</div>
			</div>
			<div class="flex margin_top">
				<div class="flex flex_between flex_1">
					<md-button
						class="md-blank"
						@click="confirmForSave('saveItem')"
						:disabled="!changeable"
					>{{ $t("investor.FilterRight.save") }}
					</md-button>
					<md-button class="md-block"
							   @click="confirmDeletion"
							   :disabled="!deletable"
					>{{ $t("common.button.delete") }}
					</md-button>
					<md-button :class="{ 'md-primary': isFormValid && changeable}"
							   @click="confirmForSave('saveAndSubmit')"
							   :disabled="!isFormValid || !changeable"
					>{{ $t("common.button.toReview")}}
					</md-button>
				</div>
			</div>
		</form>
		<UploadConfirmation @confirm="save" ref="confirmation" />
		<LinkMatchingProspectiveReceivableModal @confirm="linkToProspective" ref="linkMatchingProspectiveReceivableModal" />
  </div>
</template>
<script>
import AvailableDebtorSelect from "@/components/templates/seller/AvailableDebtorSelect.vue";
import DebtorContactSelect from "src/components/templates/select/DebtorContactSelect.vue";
import CurrencySelect from "src/components/templates/select/CurrencySelect.vue";
import CountrySelect from "src/components/templates/select/CountrySelect.vue";
import DatePicker from "src/components/templates/DatePicker";
import {validateForm, validateRequiredFields} from "src/validation/validation";

import {mapGetters} from "vuex";
import {receivableApi} from "src/common/api/receivableApi";
import {prospectiveReceivableApi} from "src/common/api/prospectiveReceivableApi";
import MoneyAmountInput from "src/components/templates/common/MoneyAmountInput";

import TypeSelect from "src/components/templates/select/TypeSelect.vue";
import {enumDef} from "src/common/enumDefs";
import AssignmentTypeSelect from "@/components/templates/select/AssignmentTypeSelect.vue";
import UploadConfirmation from "@/components/templates/seller/UploadConfirmation.vue";
import {formatAsHtml, formatAsMoneyAmount, formatAsPercentage, isValueSet} from "@/common/format";
import UploadSalesParam from "@/components/templates/seller/UploadSalesParam.vue";
import {sellPriceFormula} from "@/components/templates/seller/sell/sellPriceFormula";
import LinkMatchingProspectiveReceivableModal
	from "@/components/templates/seller/LinkMatchingProspectiveReceivableModal.vue";
import ReceivableDocumentFileUpload from "@/components/templates/seller/ReceivableDocumentFileUpload.vue";
import PdfFormDocuments from "@/components/templates/seller/PdfFormDocuments.vue";
import RetentionCheck from "@/components/templates/seller/RetentionCheck.vue";
import NoSanctionsCheck from "@/components/templates/seller/NoSanctionsCheck.vue";

export default {
	props: {
		getDetail: Function,
		onChange: Function,
	},
	components: {
		NoSanctionsCheck,
		RetentionCheck,
		PdfFormDocuments,
		ReceivableDocumentFileUpload,
		LinkMatchingProspectiveReceivableModal,
		UploadSalesParam,
		UploadConfirmation,
		AssignmentTypeSelect,
		MoneyAmountInput,
		AvailableDebtorSelect,
		DebtorContactSelect,
		CurrencySelect,
		CountrySelect,
		DatePicker,
		TypeSelect,
	},
	data() {
		let defaultForm = {
			invoiceNumber: "",
			invoiceDate: "",
			dueDate: "",
			amount: "",
			currency: "",
			applicableLaw: "",
			requestObligorConfirmation: false,
			noSanctionsConfirmation: false,
			sellWithRetention: false,
			type: enumDef.receivableType.SingleInvoice,
			disclosed: null,
			sellPriceDiscountRate: null,
			sellPriceCostOfCapitalRate: null,
			sellPriceCalculationType: enumDef.sellPriceCalculationType.None,
			offerDurationInHours: null,
			additionalDocuments: [] // Contains all required documents first, optional documents later
		};

		let defaultSellPriceSuggestion = {
			discountRate: null,
			costOfCapitalRate: null,
			debtorHasKnownRating: false,
			interestBaseRatePerAnnum: null,
			serviceFeeRate: null,
			instantPayoutInclRetention: null,
			retentionPayout: null,

			formatted: {
				interestBaseRatePerAnnum: null,
				serviceFeeRate: null,
				instantPayoutInclRetention: null,
				retentionPayout: null
			}
		};

		return {
			defaultForm: { ...defaultForm },
			form: { ...defaultForm },
			initialForm: { ...defaultForm },
			formChanged: false,
			debtorChanged: false,
			debtorInitiallySet: false,
			contactChanged: false,
			contactInitiallySet: false,
			modelValidations: {
				debtorData: {
					required: true
				},
				contactData: {
					required: true
				},
				amount: {
					required: true
				},
				currency: {
					required: true
				},
				invoiceNumber: {
					required: true
				},
				invoiceDate: {
					required: true
				},
				dueDate: {
					required: true
				},
				applicableLaw: {
					required: true
				},
				noSanctionsConfirmation: {
					checked: true
				},
				sellWithRetention: {
					checked: true
				},
				disclosed: {
					required: true
				},
				sellPriceDiscountRate: {
					required_if_func: { func: (_) => this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.Discount }
				},
				sellPriceCostOfCapitalRate: {
					required_if_func: { func: (_) => this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.CostOfCapital }
				},
			},
			fieldsIgnoredForPricingValidation: [
				"invoiceNumber", "contactData", // ignore irrelevant invoice values
				"sellPriceDiscountRate", "sellPriceCostOfCapitalRate", // ignore pricing params itself
				"noSanctionsConfirmation", "sellWithRetention" // ignore mandatory confirmations
			],
			fieldsIgnoredForSaveValidation: [
				"additionalRequiredDocument_.*" // Allow saving without required documents, but validate them on submit
			],
			fieldsIgnoredForSubmitValidation: [
				// None for now
			],
			fieldsIgnoredForChangedCheck: [
				"offerDurationInHours", // set automatically without user interaction
				"noSanctionsConfirmation", "sellWithRetention" // just confirmations required before submit
			],
			touched: false,
			saveType: "",
			saveData: null,
			invoiceNumberCheckError: "",
			defaultSellPriceSuggestion: defaultSellPriceSuggestion,
			sellPriceSuggestion: { ...defaultSellPriceSuggestion },
			retentionCheck: {
				sellWithRetention: false,
				textParams: {
					retentionInPercent: null
				}
			},
			enumDef: enumDef,
		};
	},
	created() {
		if (!this.uploadSettingsForReceivables) {
			receivableApi.inspection.getUploadSettings()
				.then(res => {
					this.$store.dispatch("setActionUploadSettings", { forReceivables: res });
				})
				.catch(this.toast);
		}
	},
	methods: {
		formatAsHtml,
		loadAvailableDebtors() {
			return receivableApi.availableDebtors();
		},
		updateRetentionCheck(retentionCheck) {
			this.retentionCheck = retentionCheck;
		},
		suggestPrice() {
			validateForm(this, this.$validator, this.fieldsIgnoredForPricingValidation)
				.then(() => {
					let receivable = this.buildReceivableData();

					receivableApi.inspection.suggestPricing(receivable)
						.then(res => this.sellPriceSuggestion = {
							...res,
							formatted: {
								interestBaseRatePerAnnum: formatAsPercentage(res.interestBaseRatePerAnnum),
								serviceFeeRate: formatAsPercentage(res.serviceFeeRate),
								instantPayoutInclRetention: formatAsMoneyAmount(res.instantPayoutInclRetention, { currency: this.form.currency }),
								retentionPayout: formatAsMoneyAmount(res.retentionPayout, { currency: this.form.currency })
							}
						})
						.catch(this.toast);
				})
				.catch(this.toast);
		},
		selectDisclosed(e) {
			this.form.disclosed = e;
			this.resetSalesParams();
		},

		selectType(e) {
			this.form.type = e;
		},
		initializeDebtor() {
			// If the receivable has no debtor yet, we try to preselect the last new one the seller has added.
			// Note that the last added data matches the data from the debtor/contact select because that already includes any relevant DSR data
			// as needed by the change logic.
			if (!this.debtorData && this.lastAddedDebtorResult) {
				this.changeDebtor(this.lastAddedDebtorResult.debtor);
				this.changeContact(this.lastAddedDebtorResult.contact);
				return;
			}

			if (this.debtorData) {
				this.debtorInitiallySet = true;

				if (!this.form.applicableLaw) {
					if (this.tableItemDetail.debtorRelationData) {
						this.form.applicableLaw = this.tableItemDetail.debtorRelationData.applicableLaw;
					}
				}

				this.autoAdjustDueDate();

				if (this.form.eInvoiceRecognized || this.form.aiRecognized) {
					this.checkForMatchingProspectiveReceivables();
				}
			}
		},
		changeDebtor(data) {
			if (this.debtorId === data.id) {
				return;
			}

			this.debtorChanged = true;
			this.$store.dispatch("setActionDebtorData", data);

			if (!this.debtorInitiallySet) {
				this.debtorInitiallySet = true;

				// Any future logic that is supposed to be triggered only the first time the debtor is set, should go here
			}

			this.checkForMatchingProspectiveReceivables();

			this.updatePdfChangeStatus();

			// Only set applicable law if it is saved in the debtor relation
			if (!this.form.applicableLaw && data.applicableLaw) {
				this.form.applicableLaw = data.applicableLaw;
			}

			this.updateDisclosedSettingFromPreferences();
			this.resetSalesParams();

			if (this.contactData) {
				this.changeContact(null);
			}

			this.autoAdjustDueDate();
		},
		changeContact(data) {
			if (!this.contactInitiallySet) {
				this.contactInitiallySet = true;
			} else {
				this.contactChanged = true;
				this.updatePdfChangeStatus();
			}

			this.$store.dispatch("setActionContactData", data);
		},
		changeInvoiceDate(e) {
			if (this.form.invoiceDate === e) {
				return;
			}

			this.form.invoiceDate = e;
			this.autoAdjustDueDate();
			this.resetSalesParams();
		},
		changeDueDate(e) {
			if (this.form.dueDate === e) {
				return;
			}

			this.form.dueDate = e;
			this.resetSalesParams();
		},
		removeItem(id, invoiceNumber, type) {
			let pList = this.pafList;
			const indexToDelete = this.pafList.findIndex(n => n.receivableId === id);
			let nextItem;
			if (indexToDelete !== -1) {
				if (type === "saveAndSubmit") {
					pList.splice(indexToDelete, 1);
				} else {
					pList[indexToDelete].invoiceNumber = invoiceNumber;
				}
			}
			if (pList.length === 0) {
				this.$store.dispatch("setActionPdfDialog", { show: false });
				return;
			} else {
				if (type === "saveAndSubmit") {
					nextItem = pList[indexToDelete];
				} else {
					nextItem = pList[indexToDelete + 1];
				}
				if (nextItem) {
					this.$store.dispatch("setActionActivePdf", nextItem);
					this.getDetail(nextItem.receivableId);
				}
			}
			this.$store.dispatch("setActionPdfList", pList);
		},
		autoAdjustDueDate() {
			if (!this.form.dueDate && this.debtorData.historicalPaymentDays && this.form.invoiceDate) {
				let automaticDueDate = moment(this.form.invoiceDate, "DD/MM/YYYY")
					.add(this.debtorData.historicalPaymentDays, "d")
					.format("DD/MM/YYYY");


				this.changeDueDate(automaticDueDate);
			}
		},
		selectCurrency(currency) {
			if (this.form.currency === currency) {
				return;
			}

			this.form.currency = currency;
			this.resetSalesParams();
		},
		onAmountChanged() {
			this.resetSalesParams();
		},
		selectSellPriceCalculationType(type) {
			this.form.sellPriceCalculationType = type;
		},
		confirmForSave(type) {
			let ignoreFields = type === "saveAndSubmit" ? this.fieldsIgnoredForSubmitValidation : this.fieldsIgnoredForSaveValidation;

			validateForm(this, this.$validator, ignoreFields)
				.then(() => {
					this.checkInvoiceNumberAvailability(() => {
						if (type === "saveAndSubmit") {
							this.saveType = type;
							this.saveData = this.buildReceivableData();

							if (this.prospective) {
								this.confirmSubmitProspective();
							} else {
								this.showConfirmation();
							}
						} else {
							this.saveType = type;
							this.saveData = this.buildReceivableData();

							this.save();
						}
					});
				})
				.catch(this.toast);
		},
		showConfirmation() {
			this.$refs.confirmation.showWithReceivable(this.saveData);
		},
		checkForMatchingProspectiveReceivables() {
			if (this.prospective) {
				return; // No conversion if this is a PR
			}

			if (this.wasProspective) {
				return; // Already converted from PR
			}

			if (isValueSet(this.tableItemDetail.status) && this.tableItemDetail.status !== enumDef.receivableStatus.Draft) {
				return; // Only newly uploaded receivables should be allowed to be converted
			}

			this.$refs.linkMatchingProspectiveReceivableModal.showWithDebtorId(this.debtorId);
		},
		confirmSubmitProspective() {
			let option = {
				title: this.$t("seller.marketSounding.submission.confirm.title"),
				msg: this.$t("seller.marketSounding.submission.confirm.text"),
				confirmFun: this.save
			};
			this.confirmMeg(option, true);
		},
		save() {
			let type = this.saveType;
			this.touched = true;

			let saveFun;
			if (!this.prospective) {
				saveFun = receivableApi[type];
			} else {
				saveFun = prospectiveReceivableApi.operation[type];
			}
			saveFun(this.saveData)
				.then(res => {
					this.toastSuccess();
					this.touched = false;

					this.onChange?.();
					this.removeItem(this.form.id, res.invoiceNumber, type);
				})
				.catch(err => {
					this.touched = false;
					this.toast(err);
				});
		},
		linkToProspective(prospectiveReceivableIdToLinkWith) {
			let params = {
				receivableId: this.form.id,
				prospectiveReceivableId: prospectiveReceivableIdToLinkWith
			};
			receivableApi.linkWithProspectiveReceivable(params)
				.then(() => {
					this.toastSuccess();

					// Update the changed receivable id ("id" and "receivableId" are both used)
					const pdfListIndex = this.pafList.findIndex(n => n.receivableId === this.form.id);
					this.pafList[pdfListIndex].id = prospectiveReceivableIdToLinkWith;
					this.pafList[pdfListIndex].receivableId = prospectiveReceivableIdToLinkWith;

					// Also mark this new patched entry as active
					this.$store.dispatch("setActionActivePdf", this.pafList[pdfListIndex]);

					// Finally load the details of the converted receivable and update this window
					this.getDetail(prospectiveReceivableIdToLinkWith);
				})
				.catch(this.toast);
		},
		buildReceivableData() {
			let option = { ...this.form };

			let { requestObligorConfirmation } = this.form;
			let { invoiceDocumentFileId, invoiceDocumentFilename } = this.activePdf;

			option.invoiceDocumentFilename = invoiceDocumentFilename;
			option.invoiceDocument = invoiceDocumentFileId;
			option.debtor = { id: this.debtorId };
			option.debtorContact = this.contactData;
			option.requestObligorConfirmation = requestObligorConfirmation;
			option.isProspective = this.prospective;

			if (this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.Discount) {
				option.sellPriceCalculationRate = this.form.sellPriceDiscountRate;
			} else if (this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.CostOfCapital) {
				option.sellPriceCalculationRate = this.form.sellPriceCostOfCapitalRate;
			} else {
				option.sellPriceCalculationType = enumDef.sellPriceCalculationType.None;
				option.sellPriceCalculationRate = null;
			}

			// Only send documents that were actually uploaded
			option.additionalDocuments = this.form.additionalDocuments.filter(file => file.fileId);

			return option;
		},
		confirmDeletion() {
			let option = {
				title: "",
				msg: this.$t("UpLoadTable.del.tip"),
				confirmFun: this.delItem,
				params: this.form.id
			};
			this.confirmMeg(option);
		},
		delItem(id) {
			let option = [id];
			this.touched = true;
			receivableApi.deleteOperation(option)
				.then(res => {
					this.touched = false;
					this.removeItem(id, res.invoiceNumber, "saveAndSubmit");
				})
				.catch(err => {
					this.touched = false;
					this.toast(err);
				});
		},
		didFormChange() {
			return this.didChange(this.form, this.initialForm);
		},
		didChange(data, initial) {
			for (let key in initial) {
				if (initial[key] !== data[key] && !this.fieldsIgnoredForChangedCheck.includes(key)) {
					return true;
				}
			}

			return false;
		},
		updatePdfChangeStatus() {
			this.activePdf.changed = this.formChanged || this.debtorChanged || this.contactChanged;
		},
		resetSalesParams() {
			this.sellPriceSuggestion = { ...this.defaultSellPriceSuggestion };
			this.form.sellPriceDiscountRate = null;
			this.form.sellPriceCostOfCapitalRate = null;
		},
		beforeRedirect() {
			this.$store.dispatch("setActionPdfDialog", { show: false });
		},
		initializeSaleParameters() {
			// Assignment type
			this.initializeDisclosedSetting();

			// Only set manual pricing parameters if they are allowed to be given by the user
			let allowUserPricing = this.tableItemDetail.saleParameters.source === enumDef.sellPriceSource.User;

			// Additional check for predefined parameters in case receivable was originally uploaded with user params allowed, but now predefined settings have been added
			let forceUserPricingReset = this.hasPredefinedSaleParameters;

			if (allowUserPricing && !forceUserPricingReset) {
				// Setup only the selected pricing parameter
				let calculationType = this.tableItemDetail.saleParameters.calculationType;
				this.form.sellPriceCalculationType = calculationType;

				if (calculationType === enumDef.sellPriceCalculationType.Discount) {
					this.form.sellPriceDiscountRate = this.tableItemDetail.saleParameters.discountRate;
				} else if (calculationType === enumDef.sellPriceCalculationType.CostOfCapital) {
					this.form.sellPriceCostOfCapitalRate = this.tableItemDetail.saleParameters.costOfCapitalRate;
				}
			} else {
				this.sellPriceSuggestion = { ...this.defaultSellPriceSuggestion };
				this.form.sellPriceCalculationType = enumDef.sellPriceCalculationType.None;
				this.form.sellPriceDiscountRate = null;
				this.form.sellPriceCostOfCapitalRate = null;
			}

			this.form.offerDurationInHours = this.tableItemDetail.saleParameters.offerDurationInHours || enumDef.SellDurationOptions.properties[enumDef.SellDurationOptions.default].value;
			this.form.sellWithRetention = this.tableItemDetail.saleParameters.sellWithRetention;
		},
		initializeDisclosedSetting() {
			if (!this.tableItemDetail.isFromInitialization) {
				// use already saved value
				this.form.disclosed = this.tableItemDetail.disclosed;
			} else {
				this.updateDisclosedSettingFromPreferences();
			}
		},
		updateDisclosedSettingFromPreferences() {
			// try to find any preselected settings
			if (isValueSet(this.assignmentTypeFromDsr)) {
				// preselect by assignmentType on the debtor level
				this.form.disclosed = this.debtorData.assignmentType;
			} else if (isValueSet(this.disclosedPreference)) {
				// preselect by general company setting
				this.form.disclosed = this.disclosedPreference;
			} else {
				// preselect as disclosed
				this.form.disclosed = true;
			}
		},
		initializeDocuments() {
			// Only update documents if requirements are already loaded
			if (this.requiredDocumentFileTypes) {
				this.$refs.documents.initializeDocumentsWithRequirements(this.form.additionalDocuments, this.requiredDocumentFileTypes);
			}
		},
		checkInvoiceNumberAvailability(onSuccess) {
			this.invoiceNumberCheckError = "";

			let check = {
				receivableId: this.form.id,
				invoiceNumber: this.form.invoiceNumber
			};
			receivableApi.inspection.checkInvoiceNumberAvailability(check)
				.then(res => {
					if (isValueSet(res.existingReceivableId)) {
						this.updateInvoiceNumberCheckError(res);
					} else {
						onSuccess();
					}
				})
				.catch(this.toast);
		},
		updateInvoiceNumberCheckError(res) {
			let errorMsgKey;
			if (res.finalRejected) {
				if (res.auctionStatus === enumDef.auctionStatus.Canceled) {
					// Resubmit is not allowed and sale was canceled
					errorMsgKey = "seller.upload.invoiceNumberCheckFailed.saleCanceledNoResubmit";
				} else {
					// Resubmit is not allowed and we have no canceled auction, so this must be a receivable rejected by CuSu in verification
					errorMsgKey = "seller.upload.invoiceNumberCheckFailed.rejectedNoResubmit";
				}
			} else {
				if (res.auctionStatus === enumDef.auctionStatus.Canceled && res.receivableStatus !== enumDef.receivableStatus.UnsoldFinished) {
					// Resubmit is allowed after the sale was canceled (and receivable is still "valid" to be submitted)
					errorMsgKey = "seller.upload.invoiceNumberCheckFailed.saleCanceledAllowResubmit";
				} else if (enumDef.receivableStatus.isRejected(res.receivableStatus)) {
					// Resubmit is allowed after the receivable was rejected by CuSu in verification
					errorMsgKey = "seller.upload.invoiceNumberCheckFailed.rejectedResubmit";
				} else {
					// Generic case when resubmit is allowed
					errorMsgKey = "seller.upload.invoiceNumberCheckFailed.alreadyUploaded";
				}
			}
			this.invoiceNumberCheckError = this.$t(errorMsgKey, { invoiceNumber: this.form.invoiceNumber });

			// Manually push this as a validation error so the field is correctly marked as invalid
			this.$validator.errors.add({
				field: "invoiceNumber",
				msg: this.$t("common.validation.messages._default")
			});
			// Scroll to the error box with the actual reason so the user can see it
			this.$nextTick(() => {
				this.$refs.invoiceNumberCheckErrorBox.scrollIntoView();
			});
		}
	},
	computed: {
		...mapGetters({
			lastAddedDebtorResult: "lastAddedDebtorResult",
			debtorData: "debtorData",
			contactData: "contactData",
			tableItemData: "tableItemData",
			tableItemDetail: "tableItemDetail",
			pafList: "pafList",
			activePdf: "activePdf",
			prospective: "pdfDialogProspective",
			uploadSettings: "uploadSettings",
		}),
		uploadSettingsForReceivables() {
			return this.uploadSettings.forReceivables;
		},
		wasProspective() {
			return !this.prospective && this.isBasedOnOpportunity;
		},
		isBasedOnOpportunity() {
			return isValueSet(this.tableItemDetail.upcomingOpportunityStatus);
		},
		isFormValid() {
			return validateRequiredFields(this.form, this.modelValidations);
		},
		debtorId() {
			return this.debtorData ? this.debtorData.id : null;
		},
		hasPredefinedSaleParameters() {
			return true; // Always true for new pricing so far
		},
		inheritedPrefs() {
			return this.uploadSettingsForReceivables ? this.uploadSettingsForReceivables.inheritedPrefs : null;
		},
		requiredDocumentFileTypes() {
			if (this.prospective) {
				return []; // For prospective receivables all documents are optional
			} else {
				return this.uploadSettingsForReceivables ? this.uploadSettingsForReceivables.requiredDocumentFileTypes : null;
			}
		},
		isBundlingAllowed() {
			return this.inheritedPrefs == null || !this.inheritedPrefs.startAuctionAfterApproving;
		},
		costOfCapitalFormulaUrl() {
			return sellPriceFormula.fileUrlForCurrentLocale();
		},
		disclosedPreference() {
			return this.$sessionStorage.get("localeUser").auctionDisclosed;
		},
		isIpuAllowed() {
			return this.form.disclosed && this.debtorData && enumDef.companyStatus.isRegisteredTrader(this.debtorData.status ?? this.debtorData.debtorStatus)
				&& !this.prospective;
		},
		changeable() {
			// When adding new receivables item details might be empty, so changing is always allowed then
			if (!isValueSet(this.tableItemDetail.id) || !isValueSet(this.tableItemDetail.status)) {
				return true;
			}
			// market sounding todo: Add proper readonly state to input fields if not changeable
			// For PRs that have been rejected the receivable status is "ProspectiveDraft" but the PR should not be editable. Instead "withdraw" is required first
			return enumDef.receivableStatus.isChangeable(this.tableItemDetail.status) && (!this.prospective || enumDef.upcomingOpportunityStatus.isDraft(this.tableItemDetail.upcomingOpportunityStatus));
		},
		deletable() {
			return enumDef.receivableStatus.isDeletable(this.tableItemDetail.status) && (!this.isBasedOnOpportunity || this.tableItemDetail.upcomingOpportunityStatus === enumDef.upcomingOpportunityStatus.New);
		},
		assignmentTypeFromDsr() {
			return this.debtorData?.assignmentType;
		}
	},
	watch: {
		tableItemDetail() {
			this.$validator.reset();
			this.invoiceNumberCheckError = "";
			this.formChanged = false;
			this.debtorChanged = false;
			this.debtorInitiallySet = false;
			this.contactChanged = false;
			this.contactInitiallySet = false;

			this.form = {
				...this.defaultForm,
				...this.tableItemDetail
			};
			this.initialForm = { ...this.form };

			let { requestObligorConfirmation } = this.form;
			this.form.requestObligorConfirmation = requestObligorConfirmation;

			if (!this.tableItemDetail) {
				return;
			}

			this.initializeDebtor();

			this.initializeSaleParameters();
			if (this.isFormValid && this.form.sellPriceCalculationType !== enumDef.sellPriceCalculationType.None) {
				this.suggestPrice();
			}

			this.initializeDocuments();
		},
		requiredDocumentFileTypes() {
			this.initializeDocuments();
		},
		form: {
			deep: true,
			handler() {
				this.formChanged = this.didFormChange();
				this.updatePdfChangeStatus();
			}
		}
	}
};
</script>